import { Combobox } from '@base-ui/react/combobox'
import { useCallback, useEffect, useImperativeHandle, useRef } from 'react'

import { Icon } from '../Icon'
import { ComboboxDropdown } from './ComboboxDropdown'
import type { ComboboxModeProps, SharedProps, SingleProps } from './types'
import { preventMouseDown } from './types'
import { useSingleComboboxState } from './useSingleComboboxState'
import { useTabSelectHighlighted } from './useTabSelectHighlighted'

export type SingleComboboxBaseProps<T> = SharedProps<T> &
  ComboboxModeProps<T> &
  Omit<SingleProps<T>, 'multiple'>

export function SingleComboboxBase<T>(props: SingleComboboxBaseProps<T>) {
  const {
    ref,
    name,
    getOptionLabel,
    filter,
    isLoading,
    required,
    readOnly,
    disabled,
    placeholder,
    autoFocus,
    classNames,
    portalToRoot = false,
    renderOption,
    emptyMessage,
  } = props

  const {
    items,
    hasInput,
    isClearable,
    singleValue,
    getOptionValue,
    handleInputChange,
    handleValueChange,
    handleClear,
  } = useSingleComboboxState(props)

  const inputRef = useRef<HTMLInputElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)

  useImperativeHandle(ref, () => ({
    clear: handleClear,
    focus: () => inputRef.current?.focus(),
    blur: () => inputRef.current?.blur(),
  }))

  useEffect(() => {
    inputRef.current?.setCustomValidity(
      required && singleValue == null ? 'Please select an option.' : '',
    )
  }, [required, singleValue])

  const isItemEqual = useCallback(
    (a: T, b: T) => getOptionValue(a) === getOptionValue(b),
    [getOptionValue],
  )

  const { handleItemHighlighted, handleTabKeyDown } =
    useTabSelectHighlighted(handleValueChange)

  const popupHasContent = items.length > 0 || isLoading || !!emptyMessage

  const captureEnter = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && items.length === 0) {
        e.preventDefault()
        e.stopPropagation()
      }
    },
    [items.length],
  )

  return (
    <Combobox.Root
      name={name}
      items={items}
      onInputValueChange={handleInputChange}
      itemToStringLabel={getOptionLabel}
      filter={filter}
      readOnly={readOnly}
      disabled={disabled}
      // @ts-expect-error autoHighlight typing in @base-ui/react is too narrow
      autoHighlight="always"
      onItemHighlighted={handleItemHighlighted}
      value={singleValue}
      onValueChange={handleValueChange}
      isItemEqualToValue={isItemEqual}
      {...(props.open !== undefined && { open: props.open })}
      {...(props.onOpenChange && { onOpenChange: props.onOpenChange })}
    >
      <div
        className={classNames?.root}
        ref={anchorRef}
        onKeyDownCapture={captureEnter}
      >
        <Combobox.Input
          ref={inputRef}
          autoComplete="off"
          className={classNames?.input}
          placeholder={placeholder}
          autoFocus={autoFocus}
          aria-required={required || undefined}
          onKeyDown={handleTabKeyDown}
        />
        {isClearable && hasInput && (
          <Combobox.Clear
            className={classNames?.clear}
            onMouseDown={preventMouseDown}
          >
            <Icon icon="times" size="xs" />
          </Combobox.Clear>
        )}
        {classNames?.trigger && (
          <Combobox.Trigger className={classNames.trigger}>
            <Icon icon="chevron-down" size="xs" />
          </Combobox.Trigger>
        )}
      </div>
      <ComboboxDropdown<T>
        classNames={classNames}
        emptyMessage={emptyMessage}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        isLoading={isLoading}
        popupHasContent={popupHasContent}
        portalContainer={
          portalToRoot ? document.getElementById('root') : undefined
        }
        renderOption={renderOption}
        positionerAnchor={anchorRef}
      />
    </Combobox.Root>
  )
}
